import React, { useEffect, useState } from 'react';
import { Grid, Theme, useMediaQuery } from '@mui/material';
import { Stats, StatsLabelTopItem } from '../../../../components/app/DashboardStats/Stats';
import {
  BLUE,
  DARK_BLUE,
  DARK_GREEN,
  DARK_RED,
  GREEN,
  RED,
} from '../../../../utils/core/functions/colors.function';
import { Asset, AssetDashboardSetting } from '../../../../types/app/asset.types';
import { useTranslation } from '../../../../contexts/translation';
import { ThemeType } from '../../../../hooks/useLocalTheme';
import { isExtraStatsLine } from '../../../../utils/core/functions/dataProcess.functions';
import { Color } from '../../../../types/app/chart.types';
import { STORAGE_KEYS } from '../../../../utils/core/localStorageKeys';

interface Props {
  currentShiftTitle: string;
  timer: string;
  state: string;
  planSum: number;
  counterQtySum: number;
  performance: number;
  performanceActualBlock: number;
  planActualBlock: number;
  scrapSum: number;
  cycleTimeActual: number;
  qtyActualBlock: number;
  dashboardConfig?: AssetDashboardSetting;
  hydraDataResult: any | null;
  isCompact?: boolean;
  localTheme?: ThemeType;
  asset: Asset;
}

export function StatsDataLoaderTop({
  currentShiftTitle,
  timer,
  state,
  planSum,
  counterQtySum,
  performance,
  performanceActualBlock,
  scrapSum,
  cycleTimeActual,
  dashboardConfig,
  hydraDataResult,
  isCompact,
  localTheme,
  asset,
}: Props) {
  const { t } = useTranslation();
  const isMobileView = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [topStatsDirection, setTopStatsDirection] = useState<any>('column');
  const [_leftColumnItems, setLeftColumnItems] = useState<StatsLabelTopItem[]>();
  const [currentBlockColor, setCurrentBlockColor] = useState<Color>();

  useEffect(() => {
    let color = localTheme === ThemeType.DARK ? DARK_GREEN : GREEN;
    let obj = window.localStorage.getItem(STORAGE_KEYS.ACTUAL_BLOCK_COLOR);
    if (obj) color = JSON.parse(obj);
    setCurrentBlockColor(color);
  }, [performanceActualBlock]);

  let leftColumnItems: StatsLabelTopItem[] = [
    {
      id: 0,
      label: t('page.dashboard.stats.left-panel-headline-1'),
      value: currentShiftTitle,
      colOffset: -1,
    },
    {
      id: 1,
      label: t('page.dashboard.stats.left-panel-headline-2'),
      value: timer,
      colOffset: +2,
    },
    {
      id: 2,
      label: t('page.dashboard.stats.left-panel-headline-3'),
      value: state,
      colOffset: -1,
    },
  ];

  let leftColumnItemsHydraCol1: StatsLabelTopItem[] = [
    {
      id: 0,
      label: t('page.dashboard.stats.left-panel-headline-4'),
      value: hydraDataResult && hydraDataResult?.machineNr ? hydraDataResult?.machineNr : ' - ',
      colOffset: -1,
    },
    {
      id: 1,
      label: t('page.dashboard.stats.left-panel-headline-5'),
      value:
        hydraDataResult && hydraDataResult?.orderOperation
          ? hydraDataResult?.orderOperation
          : ' - ',
      colOffset: +2,
    },
  ];

  let leftColumnItemsHydraCol2: StatsLabelTopItem[] = [
    {
      id: 0,
      label: t('page.dashboard.stats.left-panel-headline-6'),
      value:
        hydraDataResult && hydraDataResult.actualMachineStatus
          ? hydraDataResult.actualMachineStatus
          : ' - ',
      colOffset: -1,
    },
    {
      id: 1,
      label: t('page.dashboard.stats.left-panel-headline-7'),
      value: hydraDataResult && hydraDataResult.toolNumber ? hydraDataResult.toolNumber : ' - ',
      colOffset: +2,
    },
  ];

  let rightColumnItems: StatsLabelTopItem[] = [
    {
      id: 1,
      label: t('page.dashboard.stats.right-panel-headline-1'),
      value: `${planSum.toLocaleString()} ${asset.unitOverride || t('general.units.stats-pieces')}`,
      textColor: localTheme === ThemeType.DARK ? DARK_BLUE : BLUE,
    },
    {
      id: 2,
      label: t('page.dashboard.stats.right-panel-headline-2'),
      value: `${counterQtySum.toLocaleString()} ${
        asset.unitOverride || t('general.units.stats-pieces')
      }`,
      textColor: localTheme === ThemeType.DARK ? DARK_GREEN : GREEN,
    },
    {
      id: 3,
      label: t('page.dashboard.stats.right-panel-headline-3'),
      value: `${performance.toLocaleString()} ${
        asset.unitOverride || t('general.units.stats-pieces')
      }`,
      textColor:
        performance > 0
          ? localTheme === ThemeType.DARK
            ? DARK_GREEN
            : GREEN
          : localTheme === ThemeType.DARK
          ? DARK_RED
          : RED,
    },
    {
      id: 4,
      label: t('page.dashboard.stats.right-panel-headline-4'),
      value: `${scrapSum.toLocaleString()} ${
        asset.unitOverride || t('general.units.stats-pieces')
      }`,
      textColor: localTheme === ThemeType.DARK ? DARK_RED : RED,
    },
    {
      id: 5,
      label: t('page.dashboard.stats.right-panel-headline-5'),
      value: `${performanceActualBlock.toLocaleString()} ${
        asset.unitOverride || t('general.units.stats-pieces')
      }`,
      textColor: currentBlockColor,
    },
    {
      id: 6,
      label: t('page.dashboard.stats.right-panel-headline-6'),
      value: `${(cycleTimeActual / 1000).toLocaleString()} ${t('general.units.stats-second')}`,
      textColor:
        cycleTimeActual > 0
          ? localTheme === ThemeType.DARK
            ? DARK_GREEN
            : GREEN
          : localTheme === ThemeType.DARK
          ? DARK_RED
          : RED,
    },
  ];

  let rightColumnItemsHydraCol1: StatsLabelTopItem[] = [
    {
      id: 0,
      label: t('page.dashboard.stats.right-panel-headline-7'),
      value:
        hydraDataResult && (hydraDataResult.article || hydraDataResult.articleDescription)
          ? `${hydraDataResult.article}, ${hydraDataResult.articleDescription}`
          : ' - ',
    },
  ];

  if (!dashboardConfig?.showActualCycleTime)
    rightColumnItems = rightColumnItems.filter((x) => x.id !== 6);

  if (!dashboardConfig?.showScrap) rightColumnItems = rightColumnItems.filter((x) => x.id !== 4);
  if (!dashboardConfig?.showActualState)
    leftColumnItems = leftColumnItems.filter((x) => x.id !== 2);

  // Hydra
  if (!dashboardConfig?.showHydraMachineNumber)
    leftColumnItemsHydraCol1 = leftColumnItemsHydraCol1.filter((x) => x.id !== 0);
  if (!dashboardConfig?.showHydraOrderNumber)
    leftColumnItemsHydraCol1 = leftColumnItemsHydraCol1.filter((x) => x.id !== 1);

  if (!dashboardConfig?.showHydraStatus)
    leftColumnItemsHydraCol2 = leftColumnItemsHydraCol2.filter((x) => x.id !== 0);
  if (!dashboardConfig?.showHydraToolNumber)
    leftColumnItemsHydraCol2 = leftColumnItemsHydraCol2.filter((x) => x.id !== 1);

  if (!dashboardConfig?.showHydraPartNumber)
    rightColumnItemsHydraCol1 = rightColumnItemsHydraCol1.filter((x) => x.id !== 0);

  useEffect(() => {
    if (isCompact) {
      setTopStatsDirection('row');
      setLeftColumnItems(leftColumnItems.filter((x) => x.id !== 1));
    } else {
      setTopStatsDirection('column');
      setLeftColumnItems(leftColumnItems);
    }
  }, [isCompact, timer]);

  return (
    <Grid container>
      <Grid
        item
        xl={5}
        lg={10}
        md={12}
        style={{
          lineHeight: dashboardConfig && isExtraStatsLine(dashboardConfig) ? 1.2 : undefined,
        }}
      >
        {_leftColumnItems && (
          <Stats
            items={_leftColumnItems}
            columnsCount={3}
            direction={isMobileView ? 'row' : topStatsDirection}
            isCompact={isCompact}
          />
        )}
        {leftColumnItemsHydraCol1.length > 0 && (
          <Stats
            items={leftColumnItemsHydraCol1}
            columnsCount={3}
            direction={isMobileView ? 'row' : topStatsDirection}
            isCompact={isCompact}
          />
        )}
        {leftColumnItemsHydraCol2.length > 0 && (
          <Stats
            items={leftColumnItemsHydraCol2}
            columnsCount={3}
            direction={isMobileView ? 'row' : topStatsDirection}
            isCompact={isCompact}
          />
        )}
      </Grid>
      <Grid item xl={7} lg={10} md={12}>
        <Stats
          items={rightColumnItems}
          columnsCount={2}
          direction={'row'}
          marginTop={1}
          isCompact={isCompact}
        />
        {rightColumnItemsHydraCol1.length > 0 && (
          <Stats
            items={rightColumnItemsHydraCol1}
            columnsCount={1}
            direction={'row'}
            marginTop={1}
            isCompact={isCompact}
          />
        )}
      </Grid>
    </Grid>
  );
}
